import * as i0 from '@angular/core';
import { InjectionToken, APP_INITIALIZER, NgModule, Injectable, Inject, inject } from '@angular/core';
import * as i1 from '@auth0/auth0-angular';
import { AuthClientConfig, AuthHttpInterceptor, AuthModule as AuthModule$1 } from '@auth0/auth0-angular';
import * as i1$1 from '@angular/common/http';
import { HttpClient, HttpBackend, HTTP_INTERCEPTORS } from '@angular/common/http';
import { lastValueFrom, tap, map, throwError } from 'rxjs';
import { catchError, map as map$1 } from 'rxjs/operators';
import * as CryptoJS from 'crypto-js';

const AUTH_CONFIG = new InjectionToken('AUTH_CONFIG');
const AUTH_CONFIG_PATH = new InjectionToken('AUTH_CONFIG_PATH');

class AuthModule {
    static forRoot(authConfigPath) {
        let loadedConfig;
        const configInitializerFn = (handler, angularAuthConfig, configPath) => {
            return () => {
                const httpClient = new HttpClient(handler);
                return lastValueFrom(httpClient.get(configPath).pipe(tap((config) => {
                    const authorizationParams = config.auth0ClientConfig['authorizationParams'];
                    config.auth0ClientConfig['authorizationParams'] = setRedirectUri(authorizationParams);
                    loadedConfig = config;
                    angularAuthConfig.set(config.auth0ClientConfig);
                })));
            };
        };
        authConfigPath = authConfigPath ?? '/configs/environment.json';
        return {
            ngModule: AuthModule,
            providers: [
                {
                    provide: AUTH_CONFIG_PATH,
                    useValue: authConfigPath
                },
                {
                    provide: APP_INITIALIZER,
                    useFactory: configInitializerFn,
                    deps: [HttpBackend, AuthClientConfig, AUTH_CONFIG_PATH],
                    multi: true
                },
                {
                    provide: AUTH_CONFIG,
                    useFactory: () => loadedConfig.authConfig,
                    deps: []
                },
                { provide: HTTP_INTERCEPTORS, useClass: AuthHttpInterceptor, multi: true }
            ]
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: AuthModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "19.2.2", ngImport: i0, type: AuthModule, imports: [i1.AuthModule] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: AuthModule, providers: [AuthClientConfig], imports: [AuthModule$1.forRoot()] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: AuthModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [AuthModule$1.forRoot()],
                    providers: [AuthClientConfig]
                }]
        }] });
function setRedirectUri(authorizationParams) {
    const domain = window.location.hostname;
    let redirect_uri = authorizationParams?.redirect_uri ?? domain;
    if (typeof redirect_uri === 'object' && redirect_uri !== null) {
        if (redirect_uri.hasOwnProperty(domain)) {
            redirect_uri = redirect_uri[domain];
        }
        else {
            console.error('There was a problem fetching the redirect_uri from the configuration for the current domain. Please make sure that it is defined.');
        }
    }
    else if (typeof redirect_uri !== 'string') {
        console.error('The provided redirect_uri is neither a string nor an object. As a fallback URL, the domain is used instead.');
    }
    return {
        ...(authorizationParams || {}),
        redirect_uri
    };
}

var LoggingLevelEnum;
(function (LoggingLevelEnum) {
    LoggingLevelEnum["ERROR"] = "ERROR";
    LoggingLevelEnum["WARNING"] = "WARNING";
    LoggingLevelEnum["INFORMATION"] = "INFORMATION";
    LoggingLevelEnum["EVENT"] = "EVENT";
    LoggingLevelEnum["SUCCESSFUL"] = "SUCCESSFUL";
    LoggingLevelEnum["FAILURE"] = "FAILURE";
})(LoggingLevelEnum || (LoggingLevelEnum = {}));
var EventTypeEnum;
(function (EventTypeEnum) {
    EventTypeEnum[EventTypeEnum["Error"] = 1] = "Error";
    EventTypeEnum[EventTypeEnum["Warning"] = 2] = "Warning";
    EventTypeEnum[EventTypeEnum["Information"] = 3] = "Information";
    EventTypeEnum[EventTypeEnum["Successful"] = 4] = "Successful";
    EventTypeEnum[EventTypeEnum["Failure"] = 5] = "Failure";
})(EventTypeEnum || (EventTypeEnum = {}));

class AuthService {
    constructor(authService) {
        this.authService = authService;
        this.isLoading$ = this.authService.isLoading$;
        /**
         * Emits boolean values indicating the authentication state of the user. If `true`, it means a user has authenticated.
         * This depends on the value of `isLoading$`, so there is no need to manually check the loading state of the SDK.
         */
        this.isAuthenticated$ = this.authService.isAuthenticated$;
        /**
         * Emits errors that occur during login, or when checking for an active session on startup.
         */
        this.error$ = this.authService.error$;
        /**
         * Emits the value (if any) that was passed to the `loginWithRedirect` method call
         * but only **after** `handleRedirectCallback` is first called
         */
        this.appState$ = this.authService.appState$;
        /**
         * Emits ID token claims when authenticated, or null if not authenticated.
         */
        this.idTokenClaims$ = this.authService.idTokenClaims$;
        /**
         * Emits details about the authenticated user, or null if not authenticated.
         */
        this.user$ = this.authService.user$;
    }
    /**
     * Called when the service is destroyed
     */
    ngOnDestroy() {
        return this.authService.ngOnDestroy();
    }
    /**
     * ```js
     * handleRedirectCallback(url).subscribe(result => ...)
     * ```
     *
     * After the browser redirects back to the callback page,
     * call `handleRedirectCallback` to handle success and error
     * responses from Auth0. If the response is successful, results
     * will be valid according to their expiration times.
     *
     * Calling this method also refreshes the authentication and user states.
     *
     * @param url The URL to that should be used to retrieve the `state` and `code` values. Defaults to `window.location.href` if not given.
     */
    handleRedirectCallback$(url) {
        return this.authService.handleRedirectCallback(url);
    }
    /**
     * ```js
     * login(options);
     * ```
     *
     * Performs a redirect to `/authorize` using the parameters
     * provided as arguments. Random and secure `state` and `nonce`
     * parameters will be auto-generated.
     *
     * @param options The login options
     */
    login$(options) {
        return this.authService.loginWithRedirect(options);
    }
    /**
     * ```js
     * logout();
     * ```
     *
     * Clears the application session and performs a redirect to `/v2/logout`, using
     * the parameters provided as arguments, to clear the Auth0 session.
     * If the `federated` option is specified it also clears the Identity Provider session.
     * If the `openUrl` option is set to false, it only clears the application session.
     * It is invalid to set both the `federated` to true and `openUrl` to `false`,
     * and an error will be thrown if you do.
     * [Read more about how Logout works at Auth0](https://auth0.com/docs/logout).
     *
     * @param options The logout options
     */
    logout$(options) {
        return this.authService.logout(options);
    }
    /**
     * ```js
     * getTokenWithPopup(options).subscribe(token => ...)
     * ```
     *
     * Get an access token interactively.
     *
     * Opens a popup with the `/authorize` URL using the parameters
     * provided as arguments. Random and secure `state` and `nonce`
     * parameters will be auto-generated. If the response is successful,
     * results will be valid according to their expiration times.
     */
    getAccessTokenWithPopup$(options) {
        return this.authService.getAccessTokenWithPopup(options);
    }
    /**
     * Fetches a new access token and returns it.
     *
     * @param options The options for configuring the token fetch.
     *
     * To turn on verbose mode, add 'detailedResponse' flag to the options' properties and set it to true.
     * Example:
     * ```typescript
     * const options = {
     *    detailedResponse: true,
     *  ...
     * }
     * this.authService.getAccessTokenSilently(options);
     * ```
     */
    getAccessTokenSilently$(options = {}) {
        return this.authService.getAccessTokenSilently(options);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: AuthService, deps: [{ token: i1.AuthService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: AuthService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: AuthService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: i1.AuthService }] });

class CryptoJSUtils {
    static encrypt(obj, secretKey) {
        const jsonString = JSON.stringify(obj);
        const key = CryptoJS.enc.Utf8.parse(secretKey);
        const iv = CryptoJS.lib.WordArray.random(16);
        const encrypted = CryptoJS.AES.encrypt(jsonString, key, { iv });
        const cipherText = encrypted.toString();
        const ivString = CryptoJS.enc.Base64.stringify(iv);
        return `${ivString}:${cipherText}`;
    }
    static decrypt(encryptedObj, secretKey) {
        const [ivString, cipherText] = encryptedObj.split(':');
        const iv = CryptoJS.enc.Base64.parse(ivString);
        const key = CryptoJS.enc.Utf8.parse(secretKey);
        const decrypted = CryptoJS.AES.decrypt(cipherText, key, { iv });
        const decryptedData = decrypted.toString(CryptoJS.enc.Utf8);
        return JSON.parse(decryptedData);
    }
}

class LoggingService {
    constructor(authConfig, httpClient) {
        this.authConfig = authConfig;
        this.httpClient = httpClient;
        this.styles = {
            [LoggingLevelEnum.ERROR]: 'color: red; font-family: Monospace',
            [LoggingLevelEnum.WARNING]: 'color: orange; font-family: Monospace',
            [LoggingLevelEnum.INFORMATION]: 'color: blue; font-family: Monospace',
            [LoggingLevelEnum.EVENT]: 'color: pink; font-family: Monospace',
            [LoggingLevelEnum.SUCCESSFUL]: 'color: green; font-family: Monospace',
            [LoggingLevelEnum.FAILURE]: 'color: yellow; font-family: Monospace'
        };
    }
    logToConsole(message) {
        const messageToConsole = message;
        const loggingLevel = this.getLoggingLevel(messageToConsole.messageEventType);
        const style = this.styles[loggingLevel];
        if (messageToConsole.title) {
            console.info(`%c ${loggingLevel}: ${messageToConsole.title} ${messageToConsole.message} `, style);
        }
        else {
            console.info(`%c ${loggingLevel}: ${messageToConsole.message}`, style);
        }
    }
    logToDatabase(eventData) {
        const url = this.authConfig.logDbOptions.url;
        const requestOptions = this.authConfig.logDbOptions.requestOptions;
        return this.httpClient.post(url, eventData, requestOptions);
    }
    getLoggingLevel(eventType) {
        eventType = eventType === EventTypeEnum.Failure ? EventTypeEnum.Error : eventType;
        if ([EventTypeEnum.Error, EventTypeEnum.Warning, EventTypeEnum.Information].indexOf(eventType) > -1) {
            return LoggingLevelEnum[EventTypeEnum[eventType].toUpperCase()];
        }
        return LoggingLevelEnum.EVENT;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: LoggingService, deps: [{ token: AUTH_CONFIG }, { token: i1$1.HttpClient }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: LoggingService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: LoggingService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [AUTH_CONFIG]
                }] }, { type: i1$1.HttpClient }] });

class SessionService {
    constructor(authConfig, loggingService, authService) {
        this.authConfig = authConfig;
        this.loggingService = loggingService;
        this.authService = authService;
        this.isLoading$ = this.authService.isLoading$;
        /**
         * Emits boolean values indicating the authentication state of the user. If `true`, it means a user has authenticated.
         * This depends on the value of `isLoading$`, so there is no need to manually check the loading state of the SDK.
         */
        this.isAuthenticated$ = this.authService.isAuthenticated$;
        /**
         * Emits errors that occur during login, or when checking for an active session on startup.
         */
        this.error$ = this.authService.error$;
        /**
         * Emits the value (if any) that was passed to the `loginWithRedirect` method call
         * but only **after** `handleRedirectCallback` is first called
         */
        this.appState$ = this.authService.appState$;
        /**
         * Emits ID token claims when authenticated, or null if not authenticated.
         */
        this.idTokenClaims$ = this.authService.idTokenClaims$;
        /**
         * Emits details about the authenticated user, or null if not authenticated.
         */
        this.user$ = this.getAuthUser();
    }
    ngOnDestroy() {
        this.authService.ngOnDestroy();
    }
    /**
     * ```js
     * getTokenWithPopup(options).subscribe(token => ...)
     * ```
     *
     * Get an access token interactively.
     *
     * Opens a popup with the `/authorize` URL using the parameters
     * provided as arguments. Random and secure `state` and `nonce`
     * parameters will be auto-generated. If the response is successful,
     * results will be valid according to their expiration times.
     */
    getAccessTokenWithPopup$(options) {
        return this.authService.getAccessTokenWithPopup$(options);
    }
    /**
     * Fetches a new access token and returns it.
     *
     * @param options The options for configuring the token fetch.
     *
     * To turn on verbose mode, add 'detailedResponse' flag to the options' properties and set it to true.
     * Example:
     * ```typescript
     * const options = {
     *    detailedResponse: true,
     *  ...
     * }
     * this.authService.getAccessTokenSilently(options);
     * ```
     */
    getAccessTokenSilently(options = {}) {
        return this.authService.getAccessTokenSilently$(options);
    }
    /**
     * ```js
     * handleRedirectCallback(url).subscribe(result => ...)
     * ```
     *
     * After the browser redirects back to the callback page,
     * call `handleRedirectCallback` to handle success and error
     * responses from Auth0. If the response is successful, results
     * will be valid according to their expiration times.
     *
     * Calling this method also refreshes the authentication and user states.
     *
     * @param url The URL to that should be used to retrieve the `state` and `code` values. Defaults to `window.location.href` if not given.
     */
    handleRedirectCallback$(url) {
        return this.authService.handleRedirectCallback$(url);
    }
    /**
     * ```js
     * login(options);
     * ```
     *
     * Performs a redirect to `/authorize` using the parameters
     * provided as arguments. Random and secure `state` and `nonce`
     * parameters will be auto-generated.
     *
     * @param options The login options
     */
    login(options) {
        return this.authService.login$(options);
    }
    /**
     * ```js
     * logout();
     * ```
     *
     * Clears the application session and performs a redirect to `/v2/logout`, using
     * the parameters provided as arguments, to clear the Auth0 session.
     * If the `federated` option is specified it also clears the Identity Provider session.
     * If the `openUrl` option is set to false, it only clears the application session.
     * It is invalid to set both the `federated` to true and `openUrl` to `false`,
     * and an error will be thrown if you do.
     * [Read more about how Logout works at Auth0](https://auth0.com/docs/logout).
     *
     * @param options The logout options
     */
    logout(options) {
        if (!options) {
            const { logoutRedirectUrl } = this.authConfig;
            const domain = window.location.hostname;
            let returnTo = logoutRedirectUrl ?? domain; // Use the domain as the fallback redirect URL
            if (typeof logoutRedirectUrl === 'object' && logoutRedirectUrl !== null) {
                if (logoutRedirectUrl.hasOwnProperty(domain)) {
                    returnTo = logoutRedirectUrl[domain];
                }
                else {
                    console.error('There was a problem fetching the logoutRedirectUrl from the configuration for the current domain. Please make sure that it is defined.');
                }
            }
            else if (typeof logoutRedirectUrl !== 'string') {
                console.error('The provided logoutRedirectUrl is neither a string nor an object. As a fallback URL, the domain is used instead.');
            }
            options = {
                logoutParams: { returnTo }
            };
        }
        return this.authService.logout$(options);
    }
    /**
     * ```js
     * logToConsole();
     * ```
     * Log in console the message and title if this exist.
     * Title if optional , if you need log only a simple message you
     * can use message field but if you need give more details you
     * can use title and message.
     *
     *  @param message The message type IMessage
     * IMessage
     * ```js
     * {
     *    message: string,
     *    messageEventType: EventTypeEnum,
     *    title?: string,
     * }
     * ```
     * EvenTypeEnum
     * ```js
     * Error = 1,
     * Warning = 2,
     * Information = 3,
     * Successful = 4,
     * Failure = 5,
     * ```
     *
     *
     */
    logToConsole(message) {
        this.loggingService.logToConsole(message);
    }
    logToDatabase(message, options) {
        return this.loggingService.logToDatabase(message).pipe(tap(({ userAttributes, userHistory }) => {
            const overrideUserAttributes = options?.overrideUserAttributes ?? false; // By default, do not override the values
            const overrideUserHistory = options?.overrideUserHistory ?? true; // By default, override the values
            const userAttributesOptions = options?.userAttributesOptions;
            if (!this.userAttributes || (this.userAttributes && overrideUserAttributes)) {
                if (userAttributesOptions) {
                    const filteredUserAttributes = this.filterSelectedUserAttributes(userAttributes, userAttributesOptions);
                    this.setAuthUser({ userAttributes: filteredUserAttributes });
                }
                else {
                    this.setAuthUser({ userAttributes });
                }
            }
            if (!this.userHistory || (this.userHistory && overrideUserHistory)) {
                this.setAuthUser({ userHistory });
            }
        }));
    }
    /**
     * Set the user attributes and user history to the current user.
     * @param user The user object containing the user attributes and user history.
     * @returns void
     */
    setAuthUser(user) {
        const { userAttributes, userHistory } = user;
        const { encrypted, signingKey } = this.authConfig;
        if ('userAttributes' in user) {
            this.userAttributes = encrypted ?
                CryptoJSUtils.encrypt(userAttributes, signingKey) :
                userAttributes;
        }
        if ('userHistory' in user) {
            this.userHistory = encrypted ?
                CryptoJSUtils.encrypt(userHistory, signingKey) :
                userHistory;
        }
    }
    getAuthUser() {
        return this.authService.user$.pipe(map((user) => {
            if (user) {
                return { ...user, userAttributes: this.userAttributes, userHistory: this.userHistory };
            }
            return user;
        }));
    }
    filterSelectedUserAttributes(userAttributes, userAttributeOptions) {
        const { selectedIdentifiers, selectedAttributes } = userAttributeOptions || {};
        // Return directly if userAttributes is a string
        if (typeof userAttributes === 'string') {
            return userAttributes;
        }
        if (Array.isArray(userAttributes)) {
            return userAttributes.map(attrs => selectedAttributes?.length
                ? Object.fromEntries(Object.entries(attrs).filter(([key]) => selectedAttributes.includes(key)))
                : attrs);
        }
        const filteredUserAttributes = {};
        for (const identifier in userAttributes) {
            if (selectedIdentifiers && !selectedIdentifiers.includes(identifier))
                continue;
            const attributes = userAttributes[identifier];
            const filteredAttributes = attributes.filter(attr => !selectedAttributes || selectedAttributes.includes(attr.attribute));
            if (filteredAttributes.length) {
                filteredUserAttributes[identifier] = filteredAttributes;
            }
        }
        return filteredUserAttributes;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: SessionService, deps: [{ token: AUTH_CONFIG }, { token: LoggingService }, { token: AuthService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: SessionService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: SessionService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [AUTH_CONFIG]
                }] }, { type: LoggingService }, { type: AuthService }] });

// All the next code gets as code base from this:
// https://auth0.github.io/auth0-angular/classes/AuthGuard.html
const canMatchFunction = (_route, _segments) => redirectWhenLoggedOut(); // eslint-disable-line @typescript-eslint/no-unused-vars
const canActivateChildFunction = (_route, _state) => redirectWhenLoggedOut(); // eslint-disable-line @typescript-eslint/no-unused-vars
const canActivateFunction = (_route, _state) => redirectWhenLoggedOut(); // eslint-disable-line @typescript-eslint/no-unused-vars
function redirectWhenLoggedOut() {
    const sessionService = inject(SessionService);
    return sessionService.isAuthenticated$.pipe(catchError((error) => throwError(() => new Error(`There was an error while authenticating your access. \n${error}`))), map$1((loggedIn) => {
        if (!loggedIn) {
            const target = window.location.pathname;
            sessionService.login({
                appState: { target },
                async openUrl(url) {
                    window.location.replace(url);
                }
            });
        }
        return loggedIn;
    }));
}

/*
 * Public API Surface of auth
 */

/**
 * Generated bundle index. Do not edit.
 */

export { AUTH_CONFIG, AUTH_CONFIG_PATH, AuthModule, AuthService, CryptoJSUtils, EventTypeEnum, LoggingLevelEnum, LoggingService, SessionService, canActivateChildFunction, canActivateFunction, canMatchFunction };
